import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { showPaywall } from "@/lib/paywall";

const CreditsContext = createContext(null);

// Live credit balance + wallet for the logged-in user. Source of truth is
// GET /api/credits/wallet; call refresh() after a purchase or a credit spend.
export function CreditsProvider({ children }) {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) { setWallet(null); return; }
    try { const { data } = await api.get("/credits/wallet"); setWallet(data); } catch (e) {}
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Low-balance nudge: warn once when credits drop to the danger zone (<=2) so riders top
  // up before they're stuck mid-entry. Resets when they replenish. Skipped for the
  // professional plan (all credit actions are included there).
  const lowNudgedRef = useRef(false);
  useEffect(() => {
    if (!wallet) return;
    const c = wallet.credits;
    if (user?.role === "admin" || wallet.plan === "professional" || typeof c !== "number") return;
    if (c <= 2) {
      if (!lowNudgedRef.current) {
        lowNudgedRef.current = true;
        toast.warning(c <= 0 ? "You're out of credits" : `Only ${c} credit${c === 1 ? "" : "s"} left`, {
          description: "Top up now so you're not stuck mid-entry.",
          duration: 8000,
          action: {
            label: "Top up",
            onClick: () => showPaywall({ message: c <= 0 ? "You're out of credits. Top up to keep going." : `You're running low — ${c} credit${c === 1 ? "" : "s"} left. Top up to stay ahead.`, have: c }),
          },
        });
      }
    } else {
      lowNudgedRef.current = false;
    }
  }, [wallet, user]);

  // Let any premium action (e.g. a credit-charged follow) trigger a wallet refresh.
  useEffect(() => {
    const fn = () => refresh();
    window.addEventListener("rp-credits-refresh", fn);
    return () => window.removeEventListener("rp-credits-refresh", fn);
  }, [refresh]);

  return (
    <CreditsContext.Provider value={{
      wallet,
      credits: wallet ? wallet.credits : null,
      plan: wallet ? wallet.plan : null,
      freeTrials: wallet ? (wallet.free_trials || []) : [],
      refresh,
    }}>
      {children}
    </CreditsContext.Provider>
  );
}

export const useCredits = () => useContext(CreditsContext) || { wallet: null, credits: null, plan: null, freeTrials: [], refresh: () => {} };
